/**
 * Trovie gem mark: flat T-gem geometry and its two-tone theme pairing.
 *
 * The mark is a flat cut gem holding a T: the arch reads as the top facets, two blades read as
 * light off the pavilion, and the crossbar terminals are flat and squared (ADR-0046, superseding
 * ADR-0018's mascot ramp; DESIGN.md §7).
 *
 * The mark sits inside the palette: the blades carry the Signal hue and the body carries
 * `foreground`, so it reverses with the theme instead of holding fixed brand hexes. The values
 * below are literals because an SVG `fill` takes no className, but they are the token values, so
 * re-pointing `--primary` or `--foreground` means updating them in the same change.
 *
 * `scripts/gen-brand-assets.mjs` holds a hand-synced copy of these paths and pairings, because a
 * plain .mjs script cannot import this file (ADR-0018). Mirror any change there and re-run
 * `npm run brand:assets`, or the app icon, splash and favicon keep the old art.
 */
export type GemTheme = "light" | "dark";

export const GEM_VIEWBOX = "30 6 189 148";

const [, , GEM_W, GEM_H] = GEM_VIEWBOX.split(" ").map(Number);

/**
 * The mark's width ÷ height. The mark is wider than it is tall, so every lockup sizes off its
 * height (DESIGN.md §7). Derived from the viewBox so the two cannot drift.
 */
export const GEM_ASPECT = GEM_W / GEM_H;

/**
 * Below this height the blade geometry closes up and reads as noise, so the body renders alone
 * (DESIGN.md §7). The favicon is the main case.
 */
export const GEM_BLADE_MIN_PX = 24;

/** Light off the pavilion, painted first so the body's arch overlaps them. */
export const GEM_BLADES = [
  "M45.73 70.89 A2 2 0 0 1 48.65 71.28 L103 149 L101 149 L38.41 88.5 A8 8 0 0 1 38.86 76.6 Z",
  "M203.27 70.89 A2 2 0 0 0 200.35 71.28 L146 149 L148 149 L210.59 88.5 A8 8 0 0 0 210.14 76.6 Z",
];

/** The crown arch, then the T's two stem faces. */
export const GEM_BODY = [
  "M88.62 13.25 A9 9 0 0 1 93.96 11.5 L155.04 11.5 A9 9 0 0 1 160.38 13.25 L204.15 45.51 A7 7 0 0 1 205.69 55.21 L143 143 L184 46.5 L65 46.5 L106 143 L43.31 55.21 A7 7 0 0 1 44.85 45.51 Z",
  "M79 65.5 L113.5 65.5 L113.5 143.5 Z",
  "M170 65.5 L135.5 65.5 L135.5 143.5 Z",
];

/**
 * Blades carry the Signal and the body carries the ink, so the mark reverses with its ground.
 * `dark` is the reversed pairing, and theme-invariant surfaces use it too: the app icon, adaptive
 * icon and favicon all sit on Ink Navy (`#17243f`), a dark ground whatever the device theme
 * (DESIGN.md §7, App Icon / Splash).
 */
export const GEM_COLORS: Record<GemTheme, { blade: string; body: string; }> = {
  light: { blade: "#4463ea", body: "#20293f" },
  dark: { blade: "#7f9dff", body: "#eef1fb" },
};
